import {Box, ScrollArea, Button, Container, Grid} from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import {Dispatch, SetStateAction} from "react";
import useSWRInfinite from "swr/infinite";
import WatsonApi from "../../../backends/WatsonApi";
import {Channel} from "./Channel";
import {useStores} from "../../../stores/useStores";

export type IChannel = {
    id: number,
    title: string,
    description: string,
    type: "message" | "broadcast",
    branding: {
        type: "image" | "fontawesome" | "text",
        alt?: string,
        value: string,
    },
    timestamp: string,
}

export function useChannels(initialChannels) {
    return useSWRInfinite((pageIndex) => JSON.stringify({
        page: pageIndex,
        key: "message-channels",
    }), async (key) => {
        const parsedKey = JSON.parse(key);
        const client = await WatsonApi();
        const nextPage = parsedKey.page + 1;
        const result = await client.apis.message_list.message_list_list({
            page: nextPage,
        })
        const parsed = JSON.parse(result.data);
        return parsed.data
    }, {
        fallbackData: [initialChannels],
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateFirstPage: false,
    })
}

export default function ChannelsList(props: {
    initialChannels: IChannel[],
    totalChannels: number,
    currentChannel: IChannel | undefined,
    setCurrentChannel: Dispatch<SetStateAction<IChannel | undefined>>,
    showConversation: boolean,
    setRecipientUsers: Dispatch<SetStateAction<IUser[]>>,
    setShowConversation: Dispatch<SetStateAction<boolean>>,
}) {
    const {organizationStore} = useStores();

    const {data, isLoading, setSize, isValidating} = useChannels(props.initialChannels);

    const channels = data?.reduce((acc, curr) => [...acc, ...curr], []) || [];

    const isMoreChannels = !isLoading && props.totalChannels > channels.length;

    return <Box
        flexBasis={{
            initial: "100%",
            md: props.showConversation ? "50%" : "100%",
        }}
        height={"500px"}
        style={{backgroundColor: "var(--gray-2)", boxShadow: "var(--shadow-4)"}}
        p={"2"}
    >
        <Grid rows={"3"} height={"100%"} style={{gridTemplateRows: "auto 1fr auto"}}>
            {organizationStore.userHasRole && <Box pb={"2"}>
                <Button onClick={() => {
                    props.setRecipientUsers([]);
                    props.setCurrentChannel(undefined);
                    props.setShowConversation(true);
                }}>
                    <FontAwesome prefix={"fas"} name={"fa-plus"}/>
                    Create Message
                </Button>
            </Box>}
            <ScrollArea scrollbars={"vertical"}>
                <Container mr={"5"}>
                    {channels.map(list => {
                        return <Channel
                            channel={list}
                            currentChannelId={props.currentChannel?.id}
                            setCurrentChannel={channel => {
                                props.setCurrentChannel(channel);
                                props.setShowConversation(!!channel);
                                props.setRecipientUsers([]);
                            }}
                            key={list.id}
                        />
                    })}
                </Container>
            </ScrollArea>
            {isMoreChannels && <Box pt={"3"} style={{textAlign: "center"}}>
                <Button loading={isValidating} onClick={() => setSize(s => s + 1)}>Load More</Button>
            </Box>}
        </Grid>
    </Box>
}